import { useEffect, useMemo, useRef, useState } from "react";
import { dailyAnalysisClient } from "../data/dailyAnalysisClient";
import { DAILY_ANALYSIS_ENABLED } from "../data/dailyAnalysisConfig";
import {
  HOUSING_DETAILED_CHART_SCOPES,
  HOUSING_HTS_SCOPE_BY_ID,
  HOUSING_TAPE_CONTRACT
} from "../data/housingHtsRegistry";
import {
  loadHousingMarketChartScope,
  type HousingMarketChartScopeData,
  type HousingRtmsChartScopeId
} from "../data/housingTechnicalChart";
import {
  housingTapeClient,
  housingTapeErrorMessage,
  type HousingTapeResult
} from "../data/housingTapeClient";
import { recordRuntimeDiagnosticError, setRuntimeDiagnosticContext } from "../utils/runtimeDiagnostics";

const TAPE_SCOPES = HOUSING_DETAILED_CHART_SCOPES.filter(
  (id) => HOUSING_HTS_SCOPE_BY_ID[id].tapeCapability === "BOUNDED_ON_DEMAND"
) as readonly HousingRtmsChartScopeId[];

function eok(value: number | null | undefined, digits = 1) {
  return value == null || !Number.isFinite(value) ? "N/A" : `${(value / 100_000_000).toFixed(digits)}억`;
}

function shortGeneration(value: string | null | undefined) {
  return value ? `${value.slice(0, 10)}…` : "-";
}

export default function HousingMarketTapePanel() {
  const [scope, setScope] = useState<HousingRtmsChartScopeId>("gangnam3");
  const [chart, setChart] = useState<HousingMarketChartScopeData | null>(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState<string | null>(null);
  const [bandId, setBandId] = useState("all");
  const [month, setMonth] = useState("");
  const [pinned, setPinned] = useState<{ generationId: string; analysisAsOf: string; computedAt: string } | null>(null);
  const [result, setResult] = useState<HousingTapeResult | null>(null);
  const [querying, setQuerying] = useState(false);
  const [queryError, setQueryError] = useState<{ code: string; message: string } | null>(null);
  const queryController = useRef<AbortController | null>(null);
  const querySequence = useRef(0);

  useEffect(() => {
    if (!DAILY_ANALYSIS_ENABLED) return;
    let disposed = false;
    dailyAnalysisClient.index()
      .then((index) => { if (!disposed) setPinned(index); })
      .catch((reason) => {
        if (!disposed) {
          recordRuntimeDiagnosticError("housing-tape-ui", reason, { kind: "operation-error", source: "housing-tape:pin" });
          setQueryError({ code: "generation_unavailable", message: "현재 페이지의 일일 분석 세대를 확인하지 못했습니다." });
        }
      });
    return () => { disposed = true; };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setChart(null);
    setChartError(null);
    setChartLoading(true);
    loadHousingMarketChartScope(scope, controller.signal)
      .then((value) => setChart(value))
      .catch((reason) => {
        if (!controller.signal.aborted) setChartError(reason instanceof Error ? reason.message : String(reason));
      })
      .finally(() => { if (!controller.signal.aborted) setChartLoading(false); });
    return () => controller.abort();
  }, [scope]);

  const months = useMemo(() => Array.from(new Set((chart?.coverage.completeMonths ?? []).filter((value) => /^\d{4}-\d{2}$/.test(value)))).sort(), [chart]);
  const bandOptions = chart?.bands?.length ? chart.bands : HOUSING_TAPE_CONTRACT.bands.map(({ id, label }) => ({ id, label }));

  useEffect(() => {
    if (chart && !chart.bands.some((band) => band.id === bandId)) setBandId("all");
    setMonth((current) => current && months.includes(current) ? current : (months.at(-1) ?? ""));
  }, [chart, months, bandId]);

  useEffect(() => {
    querySequence.current += 1;
    queryController.current?.abort();
    queryController.current = null;
    setResult(null);
    setQueryError(null);
    setQuerying(false);
  }, [scope, bandId, month]);

  useEffect(() => () => queryController.current?.abort(), []);

  const runQuery = async () => {
    if (!DAILY_ANALYSIS_ENABLED || !month || querying) return;
    const sequence = ++querySequence.current;
    queryController.current?.abort();
    const controller = new AbortController();
    queryController.current = controller;
    setResult(null);
    setQueryError(null);
    setQuerying(true);
    setRuntimeDiagnosticContext("housing-tape-ui", { status: "loading", scope, bandId, month, generationId: pinned?.generationId ?? null });
    try {
      const value = await housingTapeClient.read({ scope, month, band: bandId }, controller.signal);
      if (sequence !== querySequence.current) return;
      setResult(value);
      setRuntimeDiagnosticContext("housing-tape-ui", {
        status: "ready",
        scope,
        bandId,
        month,
        generationId: value.generationId,
        rows: value.rows.length,
        totalMatchedRows: value.totalMatchedRows,
        truncated: value.truncated,
        readTelemetry: value.readTelemetry,
        sourceRevisionPinned: value.sourceRevisionPinned
      });
    } catch (reason) {
      if (controller.signal.aborted || sequence !== querySequence.current) return;
      const code = reason && typeof reason === "object" && "code" in reason ? String((reason as { code?: unknown }).code ?? "tape_unavailable") : "tape_unavailable";
      recordRuntimeDiagnosticError("housing-tape-ui", reason, { kind: "operation-error", source: `housing-tape:${scope}:${month}:${bandId}:${code}` });
      setQueryError({ code, message: housingTapeErrorMessage(reason) });
      setRuntimeDiagnosticContext("housing-tape-ui", { status: "unavailable", scope, bandId, month, generationId: pinned?.generationId ?? null, code });
    } finally {
      if (sequence === querySequence.current) {
        setQuerying(false);
        queryController.current = null;
      }
    }
  };

  const latest = result?.rows[0] ?? null;
  const scopeLabel = HOUSING_HTS_SCOPE_BY_ID[scope].label;
  const bandLabel = bandOptions.find((band) => band.id === bandId)?.label ?? bandId;

  return (
    <section className="housing-tech-chart housing-hts housing-hts-tape-view">
      <header className="housing-hts__topline">
        <div className="housing-hts__title">
          <p className="kicker">H2.7 · HOUSING HTS / TAPE</p>
          <h4>{scopeLabel} · {bandLabel}</h4>
          <div className="housing-hts__quote">
            <strong>{latest ? eok(latest.priceKrw) : "N/A"}</strong>
            <span>{result ? `${result.rows.length.toLocaleString()}건` : "미조회"}</span>
            <small>{latest ? `${latest.date} · ${latest.sigungu} ${latest.legalDong}` : "조회 버튼을 눌렀을 때만 SDP 원거래를 읽습니다."}</small>
          </div>
        </div>
        <div className="housing-tech-chart__badges">
          <span className="housing-hts__badge--basis">Generation-pinned · on-demand</span>
          <span>세대 {shortGeneration(result?.generationId ?? pinned?.generationId)}</span>
          {result?.sourceRevisionPinned && <span>exact artifact 고정</span>}
          {result?.truncated && <span className="is-warn">최대 {HOUSING_TAPE_CONTRACT.maxRows}건 표시</span>}
        </div>
      </header>

      <div className="housing-hts__toolbar">
        <label className="housing-hts__control">
          <span>지역</span>
          <select aria-label="Tape 지역" value={scope} disabled={querying} onChange={(event) => setScope(event.target.value as HousingRtmsChartScopeId)}>
            {TAPE_SCOPES.map((id) => <option key={id} value={id}>{HOUSING_HTS_SCOPE_BY_ID[id].label}</option>)}
          </select>
        </label>
        <label className="housing-hts__control">
          <span>가격대</span>
          <select aria-label="Tape 가격대" value={bandId} disabled={querying || chartLoading || !chart} onChange={(event) => setBandId(event.target.value)}>
            {bandOptions.map((band) => <option key={band.id} value={band.id}>{band.label}</option>)}
          </select>
        </label>
        <label className="housing-hts__control">
          <span>Tape 월</span>
          <select aria-label="Tape 월" value={month} disabled={querying || chartLoading || months.length === 0} onChange={(event) => setMonth(event.target.value)}>
            {months.length === 0 ? <option value="">N/A</option> : [...months].reverse().map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>
        <div className="housing-hts__toolbar-actions">
          <button type="button" className="housing-hts__tool-button" disabled={!DAILY_ANALYSIS_ENABLED || querying || chartLoading || !month || Boolean(chartError)} onClick={runQuery}>
            {querying ? "조회 중…" : "실거래 조회"}
          </button>
        </div>
      </div>

      <div className="housing-hts__basis-summary housing-hts__basis-summary--tape">
        <strong>원거래는 캐시하지 않습니다</strong>
        <span>현재 페이지에 pin된 일일 분석 세대의 RTMS `analysisIdentity`가 지정한 exact SDP artifact만 읽습니다. 최신/current partition으로 자동 대체하지 않으며, 지역·월·가격대가 바뀌면 기존 원거래는 즉시 버립니다.</span>
        <em>{pinned ? `analysisAsOf ${pinned.analysisAsOf.slice(0, 10)} · generation ${shortGeneration(pinned.generationId)}` : "게시 세대 확인 중"}</em>
      </div>

      {!DAILY_ANALYSIS_ENABLED && <div className="housing-tech-empty"><strong>Tape N/A</strong><span>이 화면은 generation-pinned daily analysis 모드에서만 원거래 Tape를 조회합니다.</span></div>}
      {chartLoading && <div className="housing-tech-empty">Tape 조회 가능 월·가격대를 확인하는 중입니다.</div>}
      {chartError && <div className="housing-tech-empty"><strong>Tape 메타데이터 N/A</strong><span>{chartError}</span><small>집계 차트는 별도 화면에서 계속 사용할 수 있습니다.</small></div>}
      {!chartLoading && !chartError && chart && months.length === 0 && <div className="housing-tech-empty"><strong>Tape 월 N/A</strong><span>현재 세대에 이 지역의 완료 월이 없습니다.</span></div>}
      {querying && <div className="housing-tech-empty">선택한 세대·지역·월의 exact RTMS artifact를 bounded read 중입니다.</div>}
      {queryError && <div className="housing-tech-empty" role="status"><strong>실거래 Tape N/A</strong><span>{queryError.message}</span><small>fail-closed · {queryError.code}</small></div>}
      {!querying && !queryError && !result && !chartLoading && !chartError && month && <div className="housing-tech-empty"><strong>실거래 Tape 대기</strong><span>{scopeLabel} · {month} · {bandLabel}</span><small>원거래는 자동 조회하지 않습니다. 필요할 때만 ‘실거래 조회’를 누릅니다.</small></div>}

      {result && <>
        <div className="housing-hts__statusbar">
          <span><strong>월</strong> {month}</span>
          <span><strong>일치 거래</strong> {result.totalMatchedRows.toLocaleString()}건</span>
          <span><strong>표시</strong> {result.rows.length.toLocaleString()}건</span>
          <span><strong>SDP read</strong> {result.readTelemetry.requests} req / {result.readTelemetry.partitions} artifacts / {result.readTelemetry.bytes.toLocaleString()} B</span>
          <span><strong>cache</strong> NO_STORE</span>
        </div>
        {result.rows.length === 0 ? <div className="housing-tech-empty"><strong>선택 조건 거래 0건</strong><span>정상 0건이며 오류로 대체한 값이 아닙니다.</span></div> :
          <div className="housing-tech-tape housing-hts__tape">
            <div className="housing-hts__pane-legend"><strong>실거래 목록</strong><span>RTMS 계약일 기준 · 최대 {HOUSING_TAPE_CONTRACT.maxRows}건</span></div>
            <div className="housing-tech-tape__scroll">
              <table>
                <thead><tr><th>일자</th><th>단지</th><th>면적</th><th>층</th><th>가격</th></tr></thead>
                <tbody>{result.rows.map((row, index) => <tr key={`${row.date}-${row.lawdCd}-${row.complexName}-${row.priceKrw}-${index}`}>
                  <td>{row.date}</td>
                  <td><strong>{row.sigungu} {row.legalDong}</strong><small>{row.complexName}</small></td>
                  <td>{row.exclusiveAreaM2.toFixed(1)}㎡</td>
                  <td>{row.floor ?? "-"}</td>
                  <td>{eok(row.priceKrw)}</td>
                </tr>)}</tbody>
              </table>
            </div>
          </div>}
      </>}
    </section>
  );
}
